import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, SearchCheckIcon, XIcon } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { navLinks as allNavLinks } from '../data/election';
import { useActiveSection, useScrolled } from '../hooks/useActiveSection';
type SiteHeaderProps = {
  hiddenHrefs?: string[];
};
export function SiteHeader({
  hiddenHrefs = []
}: SiteHeaderProps) {
  const navLinks = allNavLinks.filter(l => !hiddenHrefs.includes(l.href));
  const sectionIds = navLinks.map(l => l.href.replace('#', ''));
  const {
    active,
    navigateTo
  } = useActiveSection(sectionIds);
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    if (open) {
      // Close the menu and restore scrolling first, then scroll on the next frame
      setOpen(false);
      document.body.style.overflow = '';
      window.requestAnimationFrame(() => navigateTo(id));
    } else {
      navigateTo(id);
    }
  };
  return <header className={`sticky top-0 z-40 w-full border-b border-[#DDE3EE] bg-white transition-shadow duration-300 ${scrolled ? 'shadow-[0_4px_16px_rgba(20,30,70,0.07)]' : ''}`} data-id="element-366" data-mp="2ogt5mdvogdes">
      {/* Tricolor strip */}
      <div aria-hidden className="flex h-1 w-full" data-id="element-367" data-mp="1vt7215mg98rx">
        <span className="flex-[2] bg-accent-gold" data-id="element-368" data-mp="20ri7731suv3t" />
        <span className="flex-1 bg-primary" data-id="element-369" data-mp="20pxfrmas7sa6" />
        <span className="flex-1 bg-secondary-maroon" data-id="element-370" data-mp="25i8h8tl67m4u" />
      </div>

      {/* Fixed height — changing it while scrolling shifts the page and causes jumps */}
      <div className="mx-auto flex h-[72px] max-w-7xl items-stretch justify-between gap-6 px-6" data-id="element-371" data-mp="182bam9lwofl7">
        <a href="#inicio" onClick={e => handleNav(e, '#inicio')} aria-label="Grupo Consultor Colombiano — Inicio" className="flex shrink-0 items-center" data-id="element-372" data-mp="1d0cvn02w6ozb">
          <BrandLogo className="h-10 w-auto" data-id="element-373" data-mp="101v9f3o7suli" />
        </a>

        <nav aria-label="Principal" className="hidden lg:flex" data-id="element-374" data-mp="1erax2asg8jwz">
          <ul className="flex items-stretch" data-id="element-375" data-mp="1esvvwclsqp0n">
            {navLinks.map(l => {
            const isActive = active === l.href.replace('#', '');
            return <li key={l.href} className="flex" data-id="element-376" data-mp="m2m44z1jrkwb">
                  <a href={l.href} onClick={e => handleNav(e, l.href)} aria-current={isActive ? 'true' : undefined} className={`relative flex items-center px-3.5 text-[13px] font-bold uppercase tracking-[0.08em] transition-colors ${isActive ? 'text-primary' : 'text-foreground/60 hover:text-primary'}`} data-id="element-377" data-mp="h3stdedna7zu">
                    {l.label}
                    {isActive && <motion.span layoutId="nav-underline" className="absolute inset-x-4 -bottom-px h-[3px] bg-primary" transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 34
                }} data-id="element-378" data-mp="29z3d6f4cwnc0" />}
                  </a>
                </li>;
          })}
          </ul>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3" data-id="element-379" data-mp="2srnssuv1ovxv">
          <a href="#censo" onClick={e => handleNav(e, '#censo')} className="hidden items-center gap-2 rounded-md bg-secondary-maroon px-5 py-2.5 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition hover:bg-[#721818] focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-maroon focus-visible:ring-offset-2 sm:inline-flex" data-id="element-380" data-mp="2xppme9q3kdnl">
            <SearchCheckIcon className="h-4 w-4" data-id="element-381" data-mp="3gfyoljlvw5fa" />
            Consulta electoral
          </a>
          <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Cerrar menú' : 'Abrir menú'} className="flex h-11 w-11 items-center justify-center rounded-md text-primary transition hover:bg-primary/5 lg:hidden" data-id="element-382" data-mp="1srvnl11bl07d">
            {open ? <XIcon className="h-6 w-6" data-id="element-383" data-mp="1eu77s7m40j96" /> : <MenuIcon className="h-6 w-6" data-id="element-384" data-mp="2bi3m15u2r2h8" />}
          </button>
        </div>
      </div>

      {/* Overlay menu — positioned absolutely so it never pushes page content */}
      <AnimatePresence data-id="element-385" data-mp="26lmpbn8t7fsj">
        {open && <motion.nav id="mobile-menu" aria-label="Menú móvil" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-x-0 top-full h-[calc(100vh-77px)] overflow-y-auto border-t border-[#DDE3EE] bg-white lg:hidden" data-id="element-386" data-mp="21rs8crkl9ex5">
            <ul className="flex flex-col px-6 py-4" data-id="element-387" data-mp="c1crkru2jtqn">
              {navLinks.map((l, i) => <motion.li key={l.href} initial={{
            opacity: 0,
            x: -16
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.05 + i * 0.04
          }} data-id="element-388" data-mp="gz4s2a8s4o6g" data-mpi={i}>
                  <a href={l.href} onClick={e => handleNav(e, l.href)} className="block border-b border-[#EEF1F6] py-4 text-base font-bold uppercase tracking-[0.08em] text-primary" data-id="element-389" data-mp="uydzolix69yt">
                    {l.label}
                  </a>
                </motion.li>)}
            </ul>
            <div className="flex flex-col gap-3 px-6 pt-2" data-id="element-390" data-mp="18ozmumfep62i">
              <a href="#censo" onClick={e => handleNav(e, '#censo')} className="flex w-full items-center justify-center gap-2 rounded-md bg-secondary-maroon px-5 py-4 text-sm font-bold uppercase tracking-[0.06em] text-white" data-id="element-391" data-mp="1dpx0clph1bxo">
                <SearchCheckIcon className="h-4 w-4" data-id="element-392" data-mp="39srzg742d8yu" />
                Consulta electoral
              </a>
            </div>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}